/**
 * Duration of bonus intro screen before game starts.
 * Must match BE gameConstants.BONUS_INTRO_MS. Timer does not count down during intro.
 */
export const BONUS_INTRO_MS = 5000;

/**
 * Bonus rounds run after every Nth main question (Q5, Q10, …).
 * Must match BE `BONUS_QUESTION_INTERVAL` in gameFlowUtils.
 */
export const BONUS_QUESTION_INTERVAL = 5;

/**
 * Grace after bonus-time-up before bonus-level-result.
 * Must match BE gameConstants.BONUS_GRACE_MS. Server may extend once more if submits missing.
 */
export const BONUS_GRACE_MS = 4000;

/**
 * Host fallback wait after local results before deriving winner from score diff.
 * Must cover first grace + one automatic extension on the server.
 */
export const BONUS_HOST_RESULT_FALLBACK_MS = BONUS_GRACE_MS * 2 + 500;

/**
 * Outro after bonus-level-result before next question.
 * Must match BE gameConstants.BONUS_OUTRO_MS.
 */
export const BONUS_OUTRO_MS = 6000;

/** After this many ms from intro start, show the 3-2-1 countdown at bottom (host). */
export const BONUS_COUNTDOWN_START_MS = 2000;

/** Duration each countdown digit (3, 2, 1) is shown. */
export const BONUS_COUNTDOWN_STEP_MS = 1000;

/**
 * Bonus level game types and display labels.
 * Must match BE bonus-level-start payload.bonusGameType.
 */
export const BONUS_GAME_TYPES = [
  'puzzle',
  'word-order',
  'whos-on-image',
  'fast-typing',
  'catch-me-if-you-can',
  'fast-fingers',
  'catch-the-train',
  'quick-math',
  'letter-storm',
  'longest-word',
] as const;

export type BonusGameType = (typeof BONUS_GAME_TYPES)[number];

/**
 * Bonuses where each player submits once and the host may treat "one live answer per player" as round complete.
 * Must match `be/src/game/bonus/bonusSubmitOnceTypes.ts` (BONUS_SUBMIT_ONCE_GAME_TYPES).
 */
export const BONUS_SUBMIT_ONCE_GAME_TYPES: readonly BonusGameType[] = [
  'whos-on-image',
  'word-order',
  'puzzle',
  'fast-typing',
];

/**
 * Bonuses that stream points into the total score during play.
 * Host standings keep the pre-bonus total until results, then animate +N.
 */
export const BONUS_INCREMENTAL_SCORE_GAME_TYPES: readonly BonusGameType[] = [
  'letter-storm',
  'quick-math',
  'fast-fingers',
  'catch-the-train',
  'catch-me-if-you-can',
];

/**
 * Timer/arcade bonuses that must flush a final client score on bonus-time-up.
 * Must match BE `bonusSubmissionTypes.BONUS_TIME_UP_SUBMIT_TYPES`.
 */
export const BONUS_TIME_UP_SUBMIT_TYPES: readonly BonusGameType[] = [
  'fast-fingers',
  'fast-typing',
  'catch-the-train',
  'catch-me-if-you-can',
  'longest-word',
];

export function isBonusTimeUpSubmitType(
  gameType: string | undefined | null
): gameType is (typeof BONUS_TIME_UP_SUBMIT_TYPES)[number] {
  return !!gameType && (BONUS_TIME_UP_SUBMIT_TYPES as readonly string[]).includes(gameType);
}
/** One binary step in Quick-math (server sends expected only if we need it for host debug — omit on wire for players). */
export interface QuickMathStepPayload {
  accumulator: number;
  op: '+' | '-';
  operand: number;
  expected?: number;
}

export interface BonusLevelPayload {
  bonusGameType: BonusGameType;
  questionId: string;
  timeLimit: number;
  startTime?: number;
  questionNumber?: number;
  totalQuestions?: number;
  /** True when this bonus runs after the final normal question (last chance to win before game end). */
  isLastBonusRound?: boolean;
  /** Quick-math: full expression + precomputed steps (includes expected for validation on server; client may ignore expected). */
  numbers?: number[];
  ops?: ('+' | '-')[];
  steps?: QuickMathStepPayload[];
  [key: string]: any;
}

/** Title and short description per bonus game for intro screen — use getBonusIntro() from i18n/helpers */
export const BONUS_INTRO: Record<BonusGameType, { title: string; description: string }> = {
  puzzle: {
    title: 'Quick Puzzle',
    description: 'Solve the puzzle fast — get more points.',
  },
  'word-order': {
    title: 'Put it in the correct order',
    description: 'Drag the letters into the right order to spell the word from screen',
  },
  'whos-on-image': {
    title: "Who's on the image?",
    description: 'Guess who or what is on the image.',
  },
  'fast-typing': {
    title: 'Fast typing',
    description: 'Type as much of the text as you can before time runs out.',
  },
  'catch-me-if-you-can': {
    title: 'Save the animals',
    description: 'Catch falling animals and save them from certain death.',
  },
  'fast-fingers': {
    title: 'Fast fingers',
    description: 'Grab your phone! Get ready! Tap the good emoji as fast as you can.',
  },
  'catch-the-train': {
    title: 'Catch the train!',
    description: 'As many as you can for most points.',
  },
  'quick-math': {
    title: 'Quick-math',
    description: 'Solve each step left to right — as many as you can before time runs out.',
  },
  'letter-storm': {
    title: 'Word storm',
    description:
      'We pick a letter you type the words that start with that letter\nLooooonger the word more points you get',
  },
  'longest-word': {
    title: 'Longest word',
    description: 'Build the longest valid word you can, loooonger the word, more points',
  },
};

/**
 * Quick-math: player finished every step before time ran out.
 * Same shape as BONUS_NO_WINNER_MESSAGES; picked once per bonus questionId (see BonusLevelPlayer useMemo).
 */
export const BONUS_QUICK_MATH_CHAIN_COMPLETE_MESSAGES: Array<{ title: string; subtitle: string }> = [
  { title: 'Well done!', subtitle: 'You cleared the whole chain.' },
  { title: 'Awesome', subtitle: 'Brain on full send.' },
  { title: 'You are awesome', subtitle: 'Pythagoras would salute you.' },
  { title: 'Math machine', subtitle: "That chain didn't stand a chance." },
  { title: 'Nailed it', subtitle: 'All steps solved — rare air.' },
  { title: 'Legend', subtitle: 'The numbers bent to your will.' },
  { title: 'Speed demon', subtitle: 'Clear round with time to spare.' },
  { title: 'New Pythagoras', subtitle: 'Triangles optional; talent is not.' },
  { title: 'Genius mode', subtitle: 'The host screen is taking notes.' },
  { title: 'Clean sweep', subtitle: 'Zero steps left behind.' },
  { title: 'Sharp', subtitle: 'Like a calculator with attitude.' },
  { title: 'Brilliance', subtitle: 'The streak was right about you.' },
];

/** Messages shown when bonus round ends with no winner (title, subtitle). One is picked at random per round. */
export const BONUS_NO_WINNER_MESSAGES: Array<{ title: string; subtitle: string }> = [
  { title: "Time's up", subtitle: 'Skill issue.' },
  { title: 'Bonus: unsolved', subtitle: 'Shame: eternal.' },
  { title: 'Nobody won', subtitle: 'The Level is still laughing.' },
  { title: 'Round over', subtitle: 'Zero heroes.' },
  { title: 'Time ran out', subtitle: 'So did the brain cells, apparently.' },
  { title: 'The bar was on the floor', subtitle: 'You still missed it.' },
  { title: 'Congratulations to nobody!', subtitle: 'The bonus remains unconquered.' },
  { title: 'That was… something.', subtitle: 'Bonus has left the chat.' },
];
